import { useCallback, useEffect, useState } from "react";
import { Checkbox, message } from "antd";
import { getDrafts, deleteDraft, sendDraftEmailReminder } from "services/LoanService";
import { usePaginate } from "./usePagination";
import { DRAFTS_COLUMNS } from "components/Dashboard/Loan/AcctAcceptedLoans/appColumns";
import useModal from "./useModal";
import { ActionPopover } from "components/Drafts/ActionPopover";

export default function useDrafts() {
    const { page, setPage, limit, setLimit } = usePaginate({ baseRoute: "/drafts" });

    const [drafts, setDrafts] = useState([]);
    const [selectedDrafts, setSelectedDrafts] = useState([]);
    const [comment, setComment] = useState("");

    const [loadingStates, setLoadingStates] = useState({
        loading: false,
        deleteLoading: false,
        sendingEmailReminder: false,
    });

    const [pagination, setPagination] = useState({ total: 0, page: 1, totalPages: 1 });
    const [selectedId, setSelectedId] = useState(null);

    const [open, openDeleteModal, closeDeleteModal] = useModal(false, setSelectedId);
    const [emailReminderOpened, openEmailReminderModal, closeEmailReminderModal] = useModal(false, setSelectedId);

    const fetchData = useCallback(async () => {
        try {
            setLoadingStates(prev => ({ ...prev, loading: true }));
            const res = await getDrafts({ page, limit });
            setDrafts(res?.data?.loans ?? []);
            if (res?.data?.pagination) {
                setPagination(prev => ({ ...prev, ...res.data.pagination, total: res.data.total ?? 0 }));
            }
        } finally {
            setLoadingStates(prev => ({ ...prev, loading: false }));
        }
    }, [page, limit])

    useEffect(() => { fetchData(); }, [fetchData]);

    const toggleSelectAll = () =>
        setSelectedDrafts(isAllSelected ? [] : drafts.map(d => d.id));

    const handleSelectDrafts = id => () =>
        setSelectedDrafts(prev =>
            prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
        );

    const deleteLoanDraft = async () => {
        if (!selectedId) return;
        setLoadingStates(prev => ({ ...prev, deleteLoading: true }));
        try {
            await deleteDraft(selectedId);
            message.success("Loan deleted successfully");
            closeDeleteModal();
            fetchData();
        } finally {
            setLoadingStates(prev => ({ ...prev, deleteLoading: false }));
        }
    };

    const sendEmailReminder = async () => {
        if (!comment.trim()) return message.error("Please add a comment to your reminder");
        if (!selectedId) return;
        setLoadingStates(prev => ({ ...prev, sendingEmailReminder: true }));
        try {
            await sendDraftEmailReminder({ id: selectedId, comment });
            message.success("Email Reminder sent successfully");
            closeEmailReminderModal();
            setComment("");
        } finally {
            setLoadingStates(prev => ({ ...prev, sendingEmailReminder: false }));
        }
    };

    const isAllSelected = drafts.length && drafts.every(d => selectedDrafts.includes(d.id));
    const isAtleastOneSelected = selectedDrafts.length > 0;

    const draftColumns = [
        {
            title: "Select", render: row => (
                <Checkbox
                    checked={selectedDrafts.includes(row.id)}
                    onClick={handleSelectDrafts(row.id)}
                />
            )
        },
        ...DRAFTS_COLUMNS,
        {
            title: "Actions", render: row => (
                <ActionPopover
                    loan={row}
                    sendEmailReminder={openEmailReminderModal}
                    deleteLoanDraft={openDeleteModal}
                />
            )
        },
    ];

    const tablePagination = {
        total: pagination.total,
        current: page,
        pageSize: limit,
        onChange: setPage,
        onShowSizeChange: (_, size) => setLimit(size),
        nextIcon: () => pagination.nextPage && <button><i className="bi bi-chevron-right"></i></button>,
        prevIcon: () => pagination.prevPage && <button><i className="bi bi-chevron-left"></i></button>,
    };

    return {
        drafts,
        loading: loadingStates.loading,
        deleteLoading: loadingStates.deleteLoading,
        deleteLoanDraft,
        isAtleastOneSelected,
        open,
        total:pagination.total,
        openDeleteModal,
        closeDeleteModal,
        emailReminderOpened,
        openEmailReminderModal,
        closeEmailReminderModal,
        sendEmailReminder,
        sendingEmailReminder: loadingStates.sendingEmailReminder,
        draftColumns,
        comment,
        setComment,
        selectedDrafts,
        refetch: fetchData,
        isAllSelected,
        toggleSelectAll,
        tablePagination,
    };
}
